@import '@evisort/eds-tokens/opacity.css';

.eds-button {
  appearance: none;
  background: none;
  border: 1px solid transparent;
  /* `--_radius` lets a composing host (e.g. SplitButton) square the corners
   * where two buttons join, without reaching into this block from its own CSS. */
  border-radius: var(--_radius, var(--eds--radii-full));
  box-sizing: border-box;
  cursor: pointer;
  display: inline-flex;
  flex: none;
  font: inherit;
  max-width: 100%;
  width: var(--_width, fit-content);
  margin: 0;
  text-align: inherit;
  text-decoration: none;
  white-space: nowrap;
  transition-duration: var(--eds--transition-duration-moderate);
  transition-property:
    background-color, background-image, border-color, box-shadow, color, opacity;
  transition-timing-function: ease-out;

  --_bg: transparent;
  --_border: transparent;
  --_color: inherit;
  --_overlay-hover: linear-gradient(transparent, transparent);
  --_overlay-active: linear-gradient(transparent, transparent);
  --_color-focus: var(--_color);

  background-color: var(--_bg);
  background-image: linear-gradient(transparent, transparent);
  border-color: var(--_border);
  /* `--_color-host` is never declared here so a composing host (e.g. the
   * DataRecord hint) can own the foreground and keep it through interaction,
   * without reaching into this block. */
  color: var(--_color-host, var(--_color));
}

.eds-button > * {
  color: inherit;
}

/* Disabled state */

.eds-button:disabled,
.eds-button[aria-disabled='true'],
.eds-button[aria-busy='true'] {
  cursor: not-allowed;
  opacity: var(--eds--opacity-disabled);
}

/* Shared :focus-visible — double ring (surface gap + brand); border unchanged */

.eds-button:focus-visible:not(:disabled):not([aria-disabled='true']):not(
  [aria-busy='true']
),
.eds-button--soft-disabled:focus-visible {
  outline: none;
  box-shadow: var(--eds--interaction-focus-ring);
  color: var(--_color-host, var(--_color-focus));
}

/* Shared interaction (hover / active / focus overlay)
 * `--_overlay-hover` / `--_overlay-active` stay a transparent gradient (never
 * `none`) so transitions cross-fade — `background-image` only interpolates
 * between structurally compatible gradients, not between `none` and a
 * gradient. A composing host (e.g. the SegmentedControl thumb) suppresses
 * them with the same transparent-gradient shape and keeps the surface
 * through interaction.
 */

.eds-button:hover:not(:active):not(:disabled):not([aria-disabled='true']):not(
  [aria-busy='true']
) {
  background-image: var(--_overlay-hover);
}

.eds-button:active:not(:disabled):not([aria-disabled='true']):not(
  [aria-busy='true']
) {
  background-image: var(--_overlay-active);
  color: var(--_color-host, var(--_color-active, var(--_color)));
}

.eds-button:focus-visible:not(:active):not(:disabled):not(
  [aria-disabled='true']
):not([aria-busy='true']) {
  background-image: linear-gradient(transparent, transparent);
}

/* Sizes */

.eds-button--size-xs {
  padding: var(--eds--padding-4) var(--eds--padding-8);
}

.eds-button--size-s {
  padding: var(--eds--padding-8) var(--eds--padding-12);
}

.eds-button--size-m {
  padding: var(--eds--padding-10) var(--eds--padding-20);
}

/* `--_padding-host` is never declared here so a composing host (e.g. the
 * DataRecord hint) can collapse the icon button to its glyph, without
 * reaching into this block. */
/* `xs` keeps the 16px glyph, so 4px padding lands the square on 26px — the same
 * height as an `xs` text button, and clear of the 24x24 WCAG 2.5.8 floor. */
.eds-button--icon-only.eds-button--size-xs {
  padding: var(--_padding-host, var(--eds--padding-4));
}

.eds-button--icon-only.eds-button--size-s {
  padding: var(--_padding-host, var(--eds--padding-8));
}

.eds-button--icon-only.eds-button--size-m {
  padding: var(--_padding-host, var(--eds--padding-10));
}

/* Variants styles */

/* Primary */

.eds-button--primary {
  --_bg: var(--eds--color--bg-brand-strong);
  --_border: transparent;
  --_color: var(--eds--color--fg-inverse);
  --_overlay-hover: linear-gradient(
    var(--eds--overlay-hover-strong),
    var(--eds--overlay-hover-strong)
  );
  --_overlay-active: linear-gradient(
    var(--eds--overlay-pressed-strong),
    var(--eds--overlay-pressed-strong)
  );
}

/* Secondary */

.eds-button--secondary {
  --_bg: transparent;
  --_border: var(--eds--color--border-default);
  --_color: var(--eds--color--fg-default);
  --_overlay-hover: linear-gradient(
    var(--eds--overlay-hover-default),
    var(--eds--overlay-hover-default)
  );
  --_overlay-active: linear-gradient(
    var(--eds--overlay-pressed-default),
    var(--eds--overlay-pressed-default)
  );
  --_color-focus: var(--eds--color--fg-default);
}

/* Tertiary */

.eds-button--tertiary {
  --_bg: transparent;
  --_border: transparent;
  --_color: var(--eds--color--fg-default);
  --_overlay-hover: linear-gradient(
    var(--eds--overlay-hover-default),
    var(--eds--overlay-hover-default)
  );
  --_overlay-active: linear-gradient(
    var(--eds--overlay-pressed-default),
    var(--eds--overlay-pressed-default)
  );
}

/* Danger */

.eds-button--danger {
  --_bg: var(--eds--color--bg-critical-strong);
  --_border: transparent;
  --_color: var(--eds--color--fg-inverse);
  --_overlay-hover: linear-gradient(
    var(--eds--overlay-hover-strong),
    var(--eds--overlay-hover-strong)
  );
  --_overlay-active: linear-gradient(
    var(--eds--overlay-pressed-strong),
    var(--eds--overlay-pressed-strong)
  );
}

/* AI */

.eds-button--ai {
  --_bg: transparent;
  --_border: transparent;
  --_color: var(--eds--color--fg-inverse);
  background-color: transparent;
  background-image: var(--eds--gradient--AI);
}

.eds-button--ai:hover:not(:active):not(:disabled):not(
  [aria-disabled='true']
):not([aria-busy='true']) {
  background-color: transparent;
  background-image: var(--eds--gradient--AI-hover);
}

.eds-button--ai:active:not(:disabled):not([aria-disabled='true']):not(
  [aria-busy='true']
) {
  background-color: transparent;
  background-image: var(--eds--gradient--AI-active);
}

.eds-button--ai:focus-visible:not(:active):not(:disabled):not(
  [aria-disabled='true']
):not([aria-busy='true']) {
  background-color: transparent;
  background-image: var(--eds--gradient--AI);
}

/* Icon highlight + neutral status
 * - `.eds-button--active` — icon highlight when `aria-current` is set
 * - `[aria-current]` — CSS backup for the same tokens
 * - `.eds-button--status-neutral` — optional neutral selection treatment
 *
 * `aria-expanded` is deliberately absent: a menu trigger flips it on mousedown,
 * so highlighting an open trigger would take over its pressed treatment for as
 * long as the pointer is held. Menu triggers keep their variant's own states.
 */
.eds-button--icon-only.eds-button--active,
.eds-button--icon-only[aria-current],
.eds-button--status-neutral {
  --_bg: var(--eds--color--bg-brand-soft);
  --_border: transparent;
  --_color: var(--eds--color--fg-brand-default);
  --_overlay-hover: linear-gradient(
    var(--eds--overlay-hover-brand),
    var(--eds--overlay-hover-brand)
  );
  --_overlay-active: linear-gradient(
    var(--eds--overlay-pressed-brand),
    var(--eds--overlay-pressed-brand)
  );
  --_color-active: var(--eds--palette-blue-600);
  --_color-focus: var(--eds--color--fg-brand-default);
}

/* Positive status */

.eds-button--status-positive {
  --_bg: var(--eds--palette-green-50);
  --_border: transparent;
  --_color: var(--eds--color--fg-positive-strong);
  --_overlay-hover: linear-gradient(
    var(--eds--palette-green-100),
    var(--eds--palette-green-100)
  );
  --_overlay-active: linear-gradient(
    var(--eds--palette-green-100),
    var(--eds--palette-green-100)
  );
  --_color-active: var(--eds--color--fg-positive-strong);
  --_color-focus: var(--eds--color--fg-positive-strong);
}
